import React from "react";
import moment from "moment";
import { Box, useTheme, Typography, styled, Button } from "@material-ui/core";
import ProductDetailsModule from "./Module";
import LocalizedLink from "../../../../../components/localized-link";
import useTranslate from "../../../../../hooks/use-translate";
import { ProductDetailsPageData_datoCmsProduct_variants_variantDetail_coaReport as CoaReport } from "../../../../../templates/ProductDetailsPage/__generated__/ProductDetailsPageData";
import { analyticsTrackEvent } from "../../../../../services/analytics";

export const ReportTypography = styled(Typography)({
  fontSize: "1.2rem",
  lineHeight: "2.4rem",
  letterSpacing: "1.5px"
});

type Props = {
  data: {
    title: string;
    coaReport: CoaReport;
  };
};

const ProductDetailsModuleOilCoaReport = ({
  data: { title, coaReport }
}: Props) => {
  const theme = useTheme();
  const t = useTranslate();

  return (
    <ProductDetailsModule title={title}>
      <Box display="flex" flexDirection="column" alignItems="stretch">
        {/* Overview */}
        <Box display="flex" flexDirection="column" marginBottom={4}>
          <Typography>{t("pdp.coaReport.summarySafetyReport")}</Typography>
          {coaReport.laboratoryName && (
            <Typography color="textSecondary">{`${t("pdp.coaReport.lab")} ${
              coaReport.laboratoryName
            }`}</Typography>
          )}
          {coaReport.coaLabLicense && (
            <Typography color="textSecondary">{`${t("pdp.coaReport.license")} ${
              coaReport.coaLabLicense
            }`}</Typography>
          )}
        </Box>

        {/* Results table */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            paddingTop={0.4}
            paddingBottom={0.5}
            marginBottom={1}
            borderTop={`1px solid ${theme.palette.grey[400]}`}
            borderBottom={`1px solid ${theme.palette.grey[400]}`}
          >
            <Typography>{t("pdp.coaReport.test")}</Typography>
            <Typography>{t("pdp.coaReport.result")}</Typography>
          </Box>
          {[
            "heavyMetals",
            "microbials",
            "mycotoxins",
            "pesticides",
            "solvents"
          ].map(toxinName => {
            if (!coaReport[`${toxinName}Result`]) {
              return null;
            }
            return (
              <Box
                key={toxinName}
                display="flex"
                justifyContent="space-between"
                paddingY={0.5}
              >
                <ReportTypography>
                  {t(`pdp.coaReport.${toxinName}`).toUpperCase()}
                </ReportTypography>
                <ReportTypography color="textSecondary">
                  {t("pdp.coaReport.notDetected")}
                </ReportTypography>
              </Box>
            );
          })}
          <Box
            display="flex"
            justifyContent="space-between"
            paddingY={1}
            marginTop={1}
            borderTop={`1px solid ${theme.palette.grey[400]}`}
          >
            {coaReport.batchId && (
              <span>
                <Typography color="textSecondary" component="span">
                  {t("pdp.coaReport.batch")}
                </Typography>
                &nbsp;
                <Typography component="span">{coaReport.batchId}</Typography>
              </span>
            )}
            {coaReport.coaDate && (
              <span>
                <Typography color="textSecondary" component="span">
                  {t("pdp.coaReport.date")}
                </Typography>
                &nbsp;
                <Typography component="span">
                  {moment(coaReport.coaDate).format("ll")}
                </Typography>
              </span>
            )}
          </Box>
        </Box>

        {/* Report link */}
        <Box marginTop={4}>
          <Button
            variant="outlined"
            fullWidth
            component={LocalizedLink}
            to={coaReport.coaUrl}
            className="light"
          >
            {t("pdp.coaReport.viewCompleteAnalysis")}
          </Button>
        </Box>
      </Box>
    </ProductDetailsModule>
  );
};

export default React.memo(ProductDetailsModuleOilCoaReport);
